'use client';

/**
 * Toast Component
 *
 * A Stripe-inspired toast notification system.
 * Subtle, non-intrusive notifications that appear in the corner of the screen.
 *
 * Usage:
 * ```tsx
 * // Wrap your app with ToastProvider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Use the hook in any component
 * const toast = useToast();
 *
 * toast.success('Changes saved successfully');
 * toast.error('Failed to save changes');
 * toast.info('New update available');
 * toast.warning('Unsaved changes will be lost');
 * ```
 */

import SuccessIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { baseColors } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface ToastItem {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastContextValue {
  success: (message: string, options?: Partial<ToastItem>) => void;
  error: (message: string, options?: Partial<ToastItem>) => void;
  warning: (message: string, options?: Partial<ToastItem>) => void;
  info: (message: string, options?: Partial<ToastItem>) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
  defaultDuration?: number;
}

// ============================================================================
// Context
// ============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// ============================================================================
// Styled Components
// ============================================================================

interface ToastContainerProps {
  $position: ToastPosition;
}

const positionStyles: Record<ToastPosition, React.CSSProperties> = {
  'top-right': { top: 16, right: 16, alignItems: 'flex-end' },
  'top-left': { top: 16, left: 16, alignItems: 'flex-start' },
  'bottom-right': { bottom: 16, right: 16, alignItems: 'flex-end' },
  'bottom-left': { bottom: 16, left: 16, alignItems: 'flex-start' },
  'top-center': { top: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
  'bottom-center': { bottom: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
};

const ToastContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ToastContainerProps>(({ $position }) => ({
  position: 'fixed',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  pointerEvents: 'none',
  ...positionStyles[$position],
}));

interface ToastItemContainerProps {
  $severity: ToastSeverity;
  $isDark: boolean;
}

const severityColors: Record<ToastSeverity, { bg: string; border: string; icon: string }> = {
  success: {
    bg: baseColors.success[500],
    border: baseColors.success[400],
    icon: baseColors.success[100],
  },
  error: {
    bg: baseColors.error[500],
    border: baseColors.error[400],
    icon: baseColors.error[100],
  },
  warning: {
    bg: baseColors.warning[600],
    border: baseColors.warning[500],
    icon: baseColors.warning[100],
  },
  info: {
    bg: baseColors.primary[500],
    border: baseColors.primary[400],
    icon: baseColors.primary[100],
  },
};

const ToastItemContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ToastItemContainerProps>(({ theme, $severity, $isDark }) => {
  const colors = severityColors[$severity];
  // Stripe-style subtle surface: neutral glass background with a faint tint
  // of the severity color, severity-colored icon and accent border. The
  // toast no longer reads as a giant solid block of green/red/etc — the
  // severity is signaled by the icon + thin accent, not the fill.
  const surface = $isDark ? alpha(theme.palette.background.paper, 0.92) : alpha(theme.palette.background.paper, 0.96);
  const tintAlpha = $isDark ? 0.16 : 0.08;
  const tint = alpha(colors.bg, tintAlpha);

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: surface,
    backgroundImage: `linear-gradient(0deg, ${tint}, ${tint})`,
    boxShadow: $isDark
      ? '0 12px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.25)'
      : '0 12px 32px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06)',
    borderLeft: `3px solid ${colors.bg}`,
    border: `1px solid ${alpha(colors.border, $isDark ? 0.35 : 0.25)}`,
    borderLeftWidth: 3,
    borderLeftColor: colors.bg,
    pointerEvents: 'auto',
    minWidth: 280,
    maxWidth: 400,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
  };
});

// ============================================================================
// Toast Item Component
// ============================================================================

interface ToastItemProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const severityIcons: Record<ToastSeverity, React.ReactNode> = {
  success: <SuccessIcon />,
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  info: <InfoIcon />,
};

const ToastItemComponent: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Pick up the saturated severity tone for the icon + action link, so the
  // severity is communicated by the colored glyph rather than a giant solid
  // fill on the surface itself.
  const accent = severityColors[toast.severity].bg;

  return (
    <ToastItemContainer
      $severity={toast.severity}
      $isDark={isDark}
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <Box sx={{ color: accent, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {severityIcons[toast.severity]}
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          color: 'text.primary',
          fontWeight: 500,
        }}
      >
        {toast.message}
      </Typography>
      {toast.action && (
        <Typography
          component="button"
          onClick={toast.action.onClick}
          sx={{
            background: 'none',
            border: 'none',
            color: accent,
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            '&:hover': { opacity: 0.8 },
          }}
        >
          {toast.action.label}
        </Typography>
      )}
      <IconButton
        size="small"
        onClick={() => onDismiss(toast.id)}
        sx={{
          color: 'text.secondary',
          opacity: 0.8,
          '&:hover': {
            opacity: 1,
            color: 'text.primary',
            background: alpha(theme.palette.action.hover, 0.6),
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </ToastItemContainer>
  );
};

// ============================================================================
// Provider Component
// ============================================================================

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
  maxToasts = 5,
  defaultDuration = 4000,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback(
    (severity: ToastSeverity, message: string, options?: Partial<ToastItem>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const duration = options?.duration ?? defaultDuration;

      const newToast: ToastItem = {
        id,
        message,
        severity,
        duration,
        action: options?.action,
      };

      setToasts((prev) => {
        const updated = [newToast, ...prev];
        return updated.slice(0, maxToasts);
      });

      // Auto-dismiss
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }
    },
    [defaultDuration, maxToasts],
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      success: (message, options) => addToast('success', message, options),
      error: (message, options) => addToast('error', message, options),
      warning: (message, options) => addToast('warning', message, options),
      info: (message, options) => addToast('info', message, options),
      dismiss,
      dismissAll,
    }),
    [addToast, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Portal>
        <ToastContainer $position={position}>
          <AnimatePresence mode="popLayout">
            {toasts.map((toast) => (
              <ToastItemComponent key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
          </AnimatePresence>
        </ToastContainer>
      </Portal>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
